import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import {Link, useNavigate, useParams} from "react-router-dom";
import {
    IconArrowLeft,
    IconCalendarEventFilled,
    IconCurrentLocation,
    IconEdit,
    IconInfoCircle, IconTrashFilled,
    IconUser
} from "@tabler/icons-react";
import {
    getEventById,
    getAthleteByIdAndEvent,
    addShooting,
    getShootingsByAthleteAndEvent,
    unlinkAthleteFromEvent
} from "../../db";
import ShootingPreview5 from "../../Components/Shooting/ShootingPreview5";

const Athlete = () => {
    const { id, athlete } = useParams(); // id = eventId, athlete = athleteId
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [eventData, setEventData] = useState(null);
    const [athleteData, setAthleteData] = useState(null);
    const [shootingsData, setShootingsData] = useState([]);

    const handleNewShooting = async () => {
        if (!athleteData || !eventData) return;

        try {
            const shootingId = await addShooting({ athlete: athleteData.id, event: eventData.id }); // ✅ Use `athlete.id`
            navigate(`/shooting/${shootingId}`);
        } catch (error) {
            console.error("Error adding shooting:", error);
        }
    };

    useEffect(() => {
        const load = async () => {
            const foundEvent = await getEventById(Number(id));
            const foundAthlete = await getAthleteByIdAndEvent(Number(id), Number(athlete));

            if (foundEvent) setEventData(foundEvent);

            if (foundAthlete) {
                setAthleteData(foundAthlete);

                const foundShootings = await getShootingsByAthleteAndEvent(Number(athlete), Number(id));
                const sorted = foundShootings.sort((a, b) => b.date - a.date);

                setShootingsData(sorted);
            } else {
                navigate(`/events/${id}`, { state: { error: t("athleteEvent.notFound") }});
            }
        };

        load();
    }, [id, athlete]);

    const handleDelete = async () => {
        if (window.confirm(t("eventDetail.athlete.removeConfirmation"))) {
            await unlinkAthleteFromEvent(athleteData.id, eventData.id);
            navigate(`/events/${eventData.id}`, { state: { error: t("athleteEvent.removed") }});
        }
    };

    return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center pb-4 w-full">
                <div className="shrink-0 w-10">
                    <Link to={`/events/${eventData?.id || id}`}>
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("eventsDetail.backToDetail")}</span>
                    </Link>
                </div>
                <div className="grow flex items-center justify-center gap-2 shrink-0">
                    <IconCalendarEventFilled stroke={1.5} className="w-4 h-auto" />
                    {athleteData && athleteData.eventInfo.bib && (
                        <div className="aspect-[4/3] transform -skew-x-12 w-8 items-center justify-center inline-flex p-1 text-sm bg-[var(--light-color)] text-[var(--dark-color)]">
                            {athleteData.eventInfo.bib}
                        </div>
                    )}
                    <h1 className="text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                        {athleteData ? athleteData.nickname || `${athleteData.firstname} ${athleteData.lastname}` : '...'}
                    </h1>
                </div>
                <div className="shrink-0 w-10">
                    {athleteData && eventData && (
                    <Link to={`/events/${eventData.id}/athlete/${athleteData.id}/edit`}>
                        <IconEdit className="w-6 h-auto" />
                        <span className="sr-only">{t("event.athlete.edit")}</span>
                    </Link>
                    )}
                </div>
            </div>
            {athleteData && (
                <div className="grow overflow-y-auto overflow-x-hidden">
                    <div className="">
                        <div className="px-2 text-xs flex flex-col mb-6 sm:flex-row gap-2">
                            <Link to={`/athletes/${athleteData.id}`} className="button-primary">
                                <IconUser className="w-4 h-auto transform skew-x-12" />
                                {t("event.athlete.viewProfile")}
                            </Link>
                            <button onClick={() => handleNewShooting() } className="button-primary">
                                <IconCurrentLocation className="w-4 h-auto transform skew-x-12" />
                                {t("event.athlete.goShooting")}
                            </button>
                        </div>
                        <div className="flex flex-col gap-6">
                            <section className="flex flex-col gap-4" data-type="Info">
                                <h2 className="text-lg uppercase font-black transform -skew-x-12 border-b-2 border-[var(--light-color)] flex items-center gap-2">
                                    <IconInfoCircle className="w-6 h-auto transform skew-x-12" />
                                    <span className="grow">{t("eventDetail.generalInfo")}</span>
                                </h2>
                                <div>
                                    <table className="simple-table">
                                        <tbody>
                                        {athleteData.eventInfo.bib && (
                                            <tr>
                                                <th>{t("athlete.bib")}</th>
                                                <td>{athleteData.eventInfo.bib}</td>
                                            </tr>
                                        )}
                                        {athleteData.eventInfo.eventNote && (
                                            <tr>
                                                <th>{t("athlete.eventNote")}</th>
                                                <td>{athleteData.eventInfo.eventNote}</td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                            <section className="flex flex-col gap-4" data-type="Info">
                                <h2 className="text-lg uppercase font-black transform -skew-x-12 border-b-2 border-[var(--light-color)] flex items-center gap-2">
                                    <IconCurrentLocation className="w-6 h-auto transform skew-x-12" />
                                    <span className="grow">{t("eventDetail.shootings")}</span>
                                </h2>
                                <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 px-2 text-xs">
                                    {shootingsData.map((shooting) => (
                                        <Link
                                            key={shooting.id}
                                            to={`/shooting/${shooting.id}`}
                                            className={(shooting.type === 'race' ? 'button-primary' : 'button-secondary') + " flex-col !items-start justify-between"}
                                        >
                                            <div className="flex gap-2 items-center">
                                                {shooting.points?.length === 5 && (
                                                    <ShootingPreview5 shooting={shooting} />
                                                )}
                                                <div className="aspect-square h-4 flex items-center justify-center bg-[var(--dark-color)] border border-[var(--dark-color)] text-[var(--light-color)] text-[0.5rem] leading-none">
                                                    {t("shooting." + shooting.position)}
                                                </div>
                                                <div>
                                                    {shooting.date && (() => {
                                                        const date = new Date(shooting.date * 1000);
                                                        const hours = String(date.getHours()).padStart(2, "0");
                                                        const minutes = String(date.getMinutes()).padStart(2, "0");
                                                        return `${hours}:${minutes}`;
                                                    })()}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        </div>
                        <div className="mt-20 px-2">
                            <button className="button-secondary" onClick={() => handleDelete()}>
                                <IconTrashFilled className="w-4 h-auto transform skew-x-12" />
                                {t("eventDetail.athlete.remove")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Athlete;
